import React, { useEffect, useMemo, type FC } from 'react'
import { useIntl } from 'react-intl'
import { useAtom } from 'jotai'
import { FormElement, Select, GridCell } from 'mailup-web-components'
import { filterFormImmerAtom } from '../../state/state'
import { type ConditionProps } from '../../types'
import { type SelectOption } from '../../../../helpers/types'
import { useAtomCallback } from 'jotai/utils'
import { getDynamicFieldsCallback } from '../../callbacks/specsCallbacks'
import { dynamicFieldsAtom } from '../../state/specState'
import { DateFormat } from '../../../../types/FilterCondition'
import FormatType from './FormatType'
import ProfileFieldTypeFactory from './ProfileFieldTypeFactory'
import { PROFILE_ADDITIONAL_FIELDS } from '../../utils/conditionOptions'
import {
  type UiDateSpec,
  type UiGenericSpec,
  type UiProfileSpec,
  UiTextFormat,
  type UiTextSpec,
} from '../../filterConditionTypes'

const ProfileFieldCondition: FC<ConditionProps> = ({
  blockIndex,
  condIndex,
}) => {
  const intl = useIntl()
  const [filter, setFilter] = useAtom(filterFormImmerAtom)
  const condition = filter.Blocks[blockIndex].Conditions[condIndex]
  const spec = condition.Spec as UiProfileSpec

  const [dynamicFields] = useAtom(dynamicFieldsAtom)
  const getDynamicFields = useAtomCallback(getDynamicFieldsCallback)

  const fieldsOptions: SelectOption[] = useMemo(
    () => [
      {
        label: intl.formatMessage({ id: 'Load.Filters.IdentifierEmail' }),
        value: PROFILE_ADDITIONAL_FIELDS.EMAIL,
      },
      {
        label: intl.formatMessage({
          id: 'Load.Filters.IdentifierMobileNumber',
        }),
        value: PROFILE_ADDITIONAL_FIELDS.MOBILE,
      },
      {
        label: intl.formatMessage({
          id: 'Load.Filters.IdentifierSubscriptionDate',
        }),
        value: PROFILE_ADDITIONAL_FIELDS.SUBS_DATE,
      },
      ...dynamicFields.map<SelectOption>((f) => {
        return {
          label: f.Description,
          value: `Field${f.Id}`,
        }
      }),
    ],
    [dynamicFields, intl],
  )

  useEffect(() => {
    void getDynamicFields()
  }, [getDynamicFields])

  const handleIdentifierChange = (value: string): void => {
    if (
      value === PROFILE_ADDITIONAL_FIELDS.EMAIL ||
      value === PROFILE_ADDITIONAL_FIELDS.MOBILE
    ) {
      setFilter((draft) => {
        const cond = draft.Blocks[blockIndex].Conditions[condIndex]
        ;(cond.Spec as Partial<UiTextSpec>) = {
          Identifier: value,
          Format: UiTextFormat.Text,
          Value: undefined,
        }
      })
    } else if (value === PROFILE_ADDITIONAL_FIELDS.SUBS_DATE) {
      setFilter((draft) => {
        const cond = draft.Blocks[blockIndex].Conditions[condIndex]
        ;(cond.Spec as Partial<UiDateSpec>) = {
          Identifier: value,
          Value: '',
          Format: DateFormat.DateFormat8,
        }
      })
    } else {
      setFilter((draft) => {
        const cond = draft.Blocks[blockIndex].Conditions[condIndex]
        ;(cond.Spec as Partial<UiGenericSpec>) = {
          Identifier: value,
          Value: undefined,
        }
      })
    }
  }

  return (
    <>
      <GridCell span={3}>
        <FormElement>
          <Select
            placeholder={intl.formatMessage({
              id: 'Load.Filters.IdentifierPlaceholder',
            })}
            options={fieldsOptions}
            value={spec.Identifier}
            onChange={handleIdentifierChange}
            data-mu-test='input-select-identifier'
          />
        </FormElement>
      </GridCell>
      {spec.Identifier !== undefined &&
        !Object.values(PROFILE_ADDITIONAL_FIELDS).includes(spec.Identifier) && (
          <GridCell span={3}>
            <FormatType blockIndex={blockIndex} condIndex={condIndex} />
          </GridCell>
        )}
      {spec.Identifier !== undefined && spec.Format !== undefined && (
        <ProfileFieldTypeFactory
          format={spec.Format}
          blockIndex={blockIndex}
          condIndex={condIndex}
        />
      )}
    </>
  )
}

export default ProfileFieldCondition
